import React, { useRef } from "react"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { useNewConfirmModal } from "modules/modals/hooks/useNewConfirmModal"
import { deleteTrip, duplicateTrip, updateTrip } from "store/trips"
import { toaster } from "components/common/Toast"
import { getActionTranslations } from "utils/translations"
import { loadingSelector } from "modules/loader-watchers/selectors"
import { useShareModal } from "./useShareModal"
import { useNavigate, useSearchParams } from "react-router-dom"
import { routes } from "router"

const triggerNotification = (type, message) => {
  toaster[type](message)
}

export const useExperiencesActions = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { showConfirm,modal: confirmModal } = useNewConfirmModal()
  const { showShare, modal: shareModal } = useShareModal()
  const loading = useSelector(loadingSelector("trips.trip"))
  const translations = getActionTranslations(t)
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const isExecutingRef = useRef(false) //  Prevent duplicate actions
  const currentActionTypeRef = useRef(null)
  const currentTripIdRef = useRef(null)

  const executeAction = async (trip, actionType, action, onSuccess) => {
    if (isExecutingRef.current) return //Skip if already running
    isExecutingRef.current = true // Lock execution
    currentActionTypeRef.current = actionType
    currentTripIdRef.current = trip.id

    const { success, error } = translations[actionType]

    try {
      await dispatch(action(trip.id))
      const tripName = trip.title || trip.name || "Listing"
      const successMessage = t(`experiences.${actionType}.success`, { name: tripName, defaultValue: success })
      triggerNotification("success", successMessage)
      if (onSuccess) onSuccess()
    } catch (err) {
      console.error("executeAction error:", err)
      triggerNotification("error", error)
    } finally {
      isExecutingRef.current = false // Unlock
      currentActionTypeRef.current = null
      currentTripIdRef.current = null
    }
  }

  const handleExperiencesAction = (trip, actionType, action, onSuccess) => {
    if (isExecutingRef.current) return

    const {
      title,
      message: rawMessage,
      confirmLabel = t("global.confirm"),
      warningText
    } = translations[actionType]

    const message = actionType === "deactivate"
      ? <>Are you sure you want to deactivate <strong>&quot;{trip.title}&quot;</strong>? It will no longer be visible to guests.</>
      : rawMessage

    const isPreviewMode = searchParams.get("readOnlyPreview") === "true"
    const backRoute = searchParams.get("route")

    const afterAction = () => {
      if (onSuccess) onSuccess()
      // Back to the list tab the preview was opened from (the list reads the same ?route= value)
      if (isPreviewMode) navigate(routes.proExperiencesPath({}, backRoute ? { route: backRoute } : {}))
    }

    if (actionType === "duplicate") {
      executeAction(trip, actionType, action, afterAction)
      return
    }

    showConfirm(
      {
        title,
        message,
        ...(actionType !== "deactivate" && { highlightText: trip.title }),
        confirmLabel,
        cancelLabel: t("global.cancel"),
        ...(warningText && { warningText }),
        loading
      },
      () => executeAction(trip, actionType, action, afterAction)
    )
  }

  const isDuplicateInProgress = loading && currentActionTypeRef.current === "duplicate"
  // Generic in-progress signal for any card action (deactivate, publish, delete,
  // duplicate). The refs are set in executeAction before dispatch, and dispatch flips
  // `loading` true → re-render, so these recompute reactively. The confirm modal closes
  // immediately on confirm, so the only place to show progress is the card itself.
  const processingAction = loading ? currentActionTypeRef.current : null
  const processingTripId = loading ? currentTripIdRef.current : null

  return {
    handleDelete: (trip, onSuccess) =>
      handleExperiencesAction(trip, "delete", deleteTrip, onSuccess),
    handleDuplicate: (trip, onSuccess) =>
      handleExperiencesAction(trip, "duplicate", duplicateTrip, onSuccess),
    handleDeactivate: (trip, onSuccess) =>
      handleExperiencesAction(
        trip,
        "deactivate",
        (id) => updateTrip(id, { trip: { status: "unpublished" } }),
        onSuccess
      ),
    handleShare: showShare,
    handlePublish: (trip, onSuccess) =>
      handleExperiencesAction(
        trip,
        "publish",
        (id) => updateTrip(id, { trip: { status: "published" } }),
        onSuccess
      ),
    isDuplicateInProgress,
    duplicatingTripId: isDuplicateInProgress ? currentTripIdRef.current : null,
    processingAction,
    processingTripId,
    modal: (
      <>
        {confirmModal}
        {shareModal}
      </>
    )
  }
}
